/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import TableHeader from 'components/Table/TableHeader';

import { useTranslation } from 'react-i18next';
import * as Func from 'services/Function';

const productHeader = [
  { id: 'tb.id', label: 'No.', align: 'center', sort: false, width: 40 },
  { id: 'tb.image_id', label: 'รูป', align: 'center', sort: false, width: 40 },
  { id: 'pd.barcode', label: 'Barcode', align: 'left', sort: false, width: 130 },
  { id: 'pd.name', label: 'สินค้า', align: 'left', sort: false },
  { id: 'pd.unit_id', label: 'หน่วย', align: 'left', sort: false, width: 80 },
  { id: 'delete', label: '', align: 'center', sort: false, width: 20 },
];

function CouponProductList({
  products,
  productSearch,
  setProductSearch,
  productPage,
  setProductPage,
  productPerPage,
  setProductPerPage,
  productPageShowMenu,
  setProductPageShowMenu,
  onAddClick,
  onRemove,
}) {
  const { t } = useTranslation();

  const filtered = products.filter((p) => {
    if (!productSearch) return true;
    const q = productSearch.toLowerCase();
    return (
      (p.barcode && p.barcode.toLowerCase().includes(q)) ||
      (p.code && p.code.toLowerCase().includes(q)) ||
      (p.product_name && p.product_name.toLowerCase().includes(q)) ||
      (p.unit_name && p.unit_name.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / productPerPage));
  const safePage = Math.min(productPage, totalPages);
  const paged = filtered.slice((safePage - 1) * productPerPage, safePage * productPerPage);

  return (
    <MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={9} lg={10}>
          <MDBox noValidate component='form' role='form' onSubmit={(e) => e.preventDefault()}>
            <MDInput
              name='searchproduct'
              fullWidth
              value={productSearch}
              type='search'
              onChange={(e) => {
                setProductSearch(e.target.value);
                setProductPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={3} lg={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <MDButton color='info' onClick={onAddClick}>
            <Icon>add_circle_outline</Icon>&nbsp;เพิ่มสินค้า
          </MDButton>
        </Grid>
        <Grid item xs={12}>
          <TableContainer sx={{ boxShadow: 0, minHeight: 300 }}>
            <Table aria-label='coupon-product-table'>
              <TableHeader order='asc' orderBy='' onRequestSort={() => {}} data={productHeader} />
              <TableBody>
                {paged.length > 0 ? (
                  paged.map((p, idx) => (
                    <TableRow key={p.id} hover>
                      <TableCell align='center' width={40}>
                        {(safePage - 1) * productPerPage + idx + 1}
                      </TableCell>
                      <TableCell align='center' width={40}>
                        {Func.imageDisplay(p)}
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {p.barcode}
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox>{p.code}</MDBox>
                        <MDBox className='nowrap'>{p.product_name}</MDBox>
                      </TableCell>
                      <TableCell align='left' width={80}>
                        {p.unit_name}
                      </TableCell>
                      <TableCell align='center' width={20}>
                        <MDButton
                          variant='text'
                          color='error'
                          size='small'
                          iconOnly
                          onClick={() => onRemove(p.barcode_id)}
                        >
                          <Icon>delete</Icon>
                        </MDButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align='center'>
                      <MDTypography variant='caption' color='secondary'>
                        ยังไม่มีสินค้า
                      </MDTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12}>
          <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
            <MDButton
              variant={productPageShowMenu ? 'contained' : 'outlined'}
              color='dark'
              onClick={(e) => setProductPageShowMenu(e.currentTarget)}
            >
              {t('element.show_record', { num: productPerPage })}&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            <Menu
              anchorEl={productPageShowMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(productPageShowMenu)}
              onClose={() => setProductPageShowMenu(null)}
              keepMounted
            >
              {[10, 20, 50, 100].map((num) => (
                <MenuItem
                  key={num}
                  onClick={() => {
                    setProductPageShowMenu(null);
                    setProductPerPage(num);
                    setProductPage(1);
                  }}
                >
                  {t('element.show_record', { num })}
                </MenuItem>
              ))}
            </Menu>
            <Pagination
              count={totalPages}
              variant='outlined'
              color='secondary'
              showFirstButton
              showLastButton
              page={safePage}
              onChange={(e, p) => setProductPage(p)}
            />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

CouponProductList.defaultProps = {
  products: [],
  productSearch: '',
  productPage: 1,
  productPerPage: 10,
  productPageShowMenu: null,
};

CouponProductList.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
  productSearch: PropTypes.string,
  setProductSearch: PropTypes.func.isRequired,
  productPage: PropTypes.number,
  setProductPage: PropTypes.func.isRequired,
  productPerPage: PropTypes.number,
  setProductPerPage: PropTypes.func.isRequired,
  productPageShowMenu: PropTypes.instanceOf(Element),
  setProductPageShowMenu: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default CouponProductList;
